// Calendars and the events on them.

import type {
  Calendar,
  CalendarEvent,
  CreateCalendarInput,
  CreateEventInput,
  UpdateCalendarInput,
  UpdateEventInput,
} from "@/planning/model/wire.js";
import { api } from "@/platform/client";
import { qk } from "@/platform/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// ---- calendars / events ----
export function useCalendars() {
  return useQuery({ queryKey: qk.calendars.all, queryFn: () => api.get<Calendar[]>("/calendars") });
}

export function useEvents(range: { from?: string; to?: string }) {
  const params = new URLSearchParams();
  if (range.from) params.set("from", range.from);
  if (range.to) params.set("to", range.to);
  const qs = params.toString();
  return useQuery({
    queryKey: qk.events.range(range.from, range.to),
    queryFn: () => api.get<CalendarEvent[]>(`/events${qs ? `?${qs}` : ""}`),
  });
}

// The stored master of a series (its original DTSTART/rule), for editing — the
// range query returns expanded occurrences, which must never be saved as the
// master. Enabled only when an id is given.
export function useEvent(id: number | null) {
  return useQuery({
    queryKey: qk.events.detail(id ?? -1),
    queryFn: () => api.get<CalendarEvent>(`/events/${id}`),
    enabled: id != null,
  });
}

function invalidateCalendar(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: qk.events.all });
}

export function useCreateCalendar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCalendarInput) => api.post<Calendar>("/calendars", input),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.calendars.all }),
  });
}

export function useUpdateCalendar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: UpdateCalendarInput }) =>
      api.patch<Calendar>(`/calendars/${id}`, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.calendars.all });
      qc.invalidateQueries({ queryKey: qk.events.all });
    },
  });
}

export function useDeleteCalendar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del(`/calendars/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.calendars.all });
      invalidateCalendar(qc);
    },
  });
}

export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateEventInput) => api.post<CalendarEvent>("/events", input),
    onSuccess: () => invalidateCalendar(qc),
  });
}

// Scopes a mutation to one occurrence of a recurring series instead of the
// whole series (the default) — see docs/timezones.md.
interface OccurrenceScope {
  scope?: "this" | "all";
  occurrenceStart?: string;
}
function scopeQuery({ scope, occurrenceStart }: OccurrenceScope): string {
  if (scope !== "this") return "";
  return `?scope=this&occurrenceStart=${encodeURIComponent(occurrenceStart!)}`;
}

export function useUpdateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      patch,
      scope,
      occurrenceStart,
    }: { id: number; patch: UpdateEventInput } & OccurrenceScope) =>
      api.patch<CalendarEvent>(`/events/${id}${scopeQuery({ scope, occurrenceStart })}`, patch),
    onSuccess: () => invalidateCalendar(qc),
  });
}

export function useDeleteEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, scope, occurrenceStart }: { id: number } & OccurrenceScope) =>
      api.del(`/events/${id}${scopeQuery({ scope, occurrenceStart })}`),
    onSuccess: () => invalidateCalendar(qc),
  });
}
